// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useContext, useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { UNSAFE_LocationContext } from 'react-router-dom';
import {
  type AdvisorPageContext,
  type AdvisorPageEntityIds,
  type AdvisorPageSelection,
  type AdvisorPageType,
  unknownPageContext,
} from '../types/pageContext';

/**
 * Defensive replacement for {@code useLocation()}: returns the
 * pathname when a {@code <Router>} is in scope, falls back to
 * {@code window.location.pathname} (or {@code '/'} in jsdom)
 * otherwise. We use the UNSAFE_LocationContext internal so the hook
 * doesn't throw in test files that mount a page without wrapping
 * it in a Router — that requirement was introduced in this PR and we
 * don't want to retrofit dozens of legacy specs.
 */
function useSafeLocationPath(): string {
  const ctx = useContext(UNSAFE_LocationContext as unknown as React.Context<{ location?: { pathname?: string } } | null>);
  if (ctx?.location?.pathname) return ctx.location.pathname;
  if (typeof window !== 'undefined' && window.location?.pathname) {
    return window.location.pathname;
  }
  return '/';
}

/**
 * Module-level store for the currently-active {@link AdvisorPageContext}.
 *
 * <p>We use an external store (instead of a React context provider
 * wrapping every page's JSX) because:</p>
 * <ul>
 *   <li>The companion launcher lives at the App root — outside any
 *       individual page's tree. A context wouldn't propagate from a
 *       child page back up to the dock.</li>
 *   <li>Pages often have 500+ lines of JSX between their function body
 *       and the {@code return} statement. A one-line hook call to
 *       {@code usePageContextRegister} is dramatically less invasive
 *       than wrapping the whole return.</li>
 *   <li>{@link useSyncExternalStore} keeps the dock's subscription
 *       cheap — the dock re-renders only when the context actually
 *       changes (route navigation, entity-id resolution).</li>
 * </ul>
 */
type Listener = () => void;
const listeners = new Set<Listener>();
let current: AdvisorPageContext = unknownPageContext('/');

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): AdvisorPageContext {
  return current;
}

function setStore(next: AdvisorPageContext): void {
  // Reference equality is enough — pages register a freshly-built
  // object on every render where the inputs change.
  if (next === current) return;
  current = next;
  listeners.forEach((l) => l());
}

function clearStore(url: string): void {
  setStore(unknownPageContext(url));
}

export interface RegisterPageContextProps {
  pageType: AdvisorPageType;
  entityIds?: AdvisorPageEntityIds;
  selection?: AdvisorPageSelection;
  pageTitle?: string;
}

/**
 * Declare the page Fin should resolve "this" / "here" pronouns against.
 * Call this hook near the top of any Advisor UI page component:
 *
 * ```tsx
 * function RelationshipDetailPage() {
 *   const { id } = useParams();
 *   const [profile, setProfile] = useState<Profile | null>(null);
 *   // …
 *   usePageContextRegister({
 *     pageType: 'RELATIONSHIP_DETAIL',
 *     entityIds: { profileId: id },
 *     pageTitle: profile?.profileName,
 *   });
 *   return <div>…</div>;
 * }
 * ```
 *
 * On unmount or route change, the store reverts to UNKNOWN so the next
 * page starts clean — pages that haven't migrated yet leave the
 * advisor without a current-page anchor, and Fin will ask for the
 * missing entity name explicitly.
 */
export function usePageContextRegister(props: RegisterPageContextProps): void {
  const pathname = useSafeLocationPath();
  // Serialise the props so the effect re-fires only when actual values
  // change — caller doesn't need to memoise the props object.
  const key = JSON.stringify({ ...props, url: pathname });
  useEffect(() => {
    setStore({
      pageType: props.pageType,
      entityIds: props.entityIds,
      selection: props.selection,
      url: pathname,
      pageTitle: props.pageTitle,
    });
    return () => clearStore(pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/**
 * Read the current page's context. Subscribers re-render whenever the
 * store changes (route navigation, entity resolution settling, page
 * unmount). Returns an UNKNOWN context when no page has registered.
 */
export function useAdvisorPageContext(): AdvisorPageContext {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Optional declarative wrapper for pages that prefer JSX-wrapping over
 * the hook. Mounted/unmounted with the page; identical semantics.
 */
export function PageContextProvider({
  value,
  children,
}: {
  value: RegisterPageContextProps;
  children: ReactNode;
}) {
  usePageContextRegister(value);
  return <>{children}</>;
}

/** Test helper — reset between specs. */
export function __resetPageContextStoreForTests(): void {
  current = unknownPageContext('/');
  listeners.forEach((l) => l());
}
